import React from "react";
import {
  Dialog,
  Card,
  CardHeader,
  Input,
  Typography,
  Button,
  CardBody,
  CardFooter,
  IconButton,
  Checkbox,
  Textarea,
  Option,
  Select,
  Chip,
} from "@material-tailwind/react";
import {
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import {
  createNewEvidence,
  getAuditData,
  getStandardData,
} from "../../helperFunctions/apiFunction";
import { Link, useNavigate } from "react-router-dom";
import { useAuthUserToken } from "../../hooks/useAuthUserToken";
import toast from "react-hot-toast";
import TableSkeleton from "../../components/Loaders/TableSkeleton";
import { useLocation } from "react-router-dom";
import { ArrowDownIcon } from "@heroicons/react/24/solid";
import ExcelJS from "exceljs";
import TableRowWithShowMore from "../../components/TableRowWithShowMore";

export default function AuditEvidence() {
  const { completeAuthData: authData, refreshToken } = useAuthUserToken();
  const [templateData, setTemplateData] = useState([]);
  const [isLoading, setIsLoading] = useState(null);
  const [searchBy, setSearchBy] = useState("");
  const [sortBy, setSortBy] = useState("asc");
  const [status, setStatus] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [selectCheckbox, setSelectCheckbox] = useState([]);
  const [errors, setErrors] = useState({ name: "" });
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [standard, setStandard] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedAuditStandard, setSelectedAuditStandard] = useState("");
  const location = useLocation();
  const { selectedAuditId } = location.state || {};
  const [tableHead, setTableHead] = useState([]);

  const formSetData = {
    AuditId: selectedAuditId,
    TaskRequirements: [],
    ControlId: [],
    TaskAssignee: "Not Assigned",
    TaskCreatedBy: authData?.userName,
  };

  const [formData, setFormData] = useState(formSetData);
  useEffect(() => {
    setIsLoading(null);
    getAuditData(refreshToken, searchBy, sortBy, limit, page, status).then(
      (data) => {
        if (data?.data?.code === 440)
          toast.error(data?.data?.message, {
            className: "text-[12px]",
          });
        const filteredData = data?.data?.results.filter(
          (item) => item.auditName === selectedAuditId
        );
        if (filteredData?.length > 0) {
          setSelectedAuditStandard(filteredData[0].auditStandard);
        }
        setAuditData(filteredData);
        setTemplateData(data?.data?.results);
      }
    );
    let newTableHead = [];
    if (selectedAuditStandard) {
      let endpoint = "";
      if (selectedAuditStandard === "PCI DSS v4.0") {
        endpoint = "pcidssv4data";
        newTableHead = [
          "Req Id",
          "Requirement",
          "Description",
          "Section Id",
          "Description",
          "Control Id",
          "Description",
          "Evidence",
          "Request More Evidence",
        ];
      } else if (selectedAuditStandard === "PCI DSS v3.2.1") {
        endpoint = "pcidssv321data";
        newTableHead = [
          "Req Id",
          "Requirement",
          "Description",
          "Control Id",
          "Description",
          "Evidence",
          "Requrest More Evidence",
        ];
      } else if (selectedAuditStandard === "ISO 27001") {
        endpoint = "iso27001data";
      }

      setTableHead(newTableHead);
      getStandardData(
        refreshToken,
        endpoint,
        searchBy,
        sortBy,
        limit,
        page,
        status
      ).then((data) => {
        if (data?.code === 440)
          toast.error(data?.message, {
            className: "text-[12px]",
          });
        setStandard(data?.data);
        setTotalPage(data?.totalPages);
        setIsLoading(false);
      });
    }
  }, [
    page,
    limit,
    sortBy,
    searchBy,
    status,
    selectedAuditId,
    selectedAuditStandard,
  ]);

  const handleCheckboxChange = (e, controlId, _id) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      setSelectCheckbox((prevSelected) => [
        ...prevSelected,
        { controlId, _id },
      ]);
      setFormData((prevFormData) => ({
        ...prevFormData,
        ControlId: [...prevFormData.ControlId, controlId],
      }));
    } else {
      setSelectCheckbox((prevSelected) =>
        prevSelected.filter((item) => item.controlId !== controlId)
      );
      setFormData((prevFormData) => ({
        ...prevFormData,
        ControlId: prevFormData.ControlId.filter((id) => id !== controlId),
      }));
    }
  };

  const handleNextStep = () => {
    setOpen((cur) => !cur);
  };

  const handleChange = (e, controlId) => {
    const { value } = e.target;
    setErrors((prevErrors) => ({ ...prevErrors, [controlId]: "" }));
    setFormData((prevData) => {
      const updatedTaskRequirements = prevData.TaskRequirements.map((task) =>
        task.id === controlId ? { ...task, requirement: value } : task
      );
      const isNew = !prevData.TaskRequirements.some(
        (task) => task.id === controlId
      );
      if (isNew) {
        updatedTaskRequirements.push({ id: controlId, requirement: value });
      }
      return { ...prevData, TaskRequirements: updatedTaskRequirements };
    });
  };

  const handleBlur = () => {
    validateForm();
  };

  const validateForm = () => {
    const newErrors = {};

    selectCheckbox.forEach((item) => {
      const task = formData.TaskRequirements.find(
        (task) => task.id === item.controlId
      );
      const requirement = task?.requirement?.trim();

      if (!requirement) {
        newErrors[item.controlId] = "Evidence Task Requirements is required";
      }
    });

    setErrors(newErrors);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    validateForm();
    if (Object.keys(errors).length === 0) {
      setIsLoadingButton(true);
      createNewEvidence(formData, refreshToken)
        .then((response) => {
          if (response?.code === 200 || response?.code === 201) {
            setFormData(formSetData);
            toast.success("Evidence created successfully", {
              className: "text-[12px]",
            });
            setOpen((cur) => !cur);
          } else if (response.code === 400) {
            toast.error(response?.message, {
              className: "text-[12px]",
            });
          } else {
            toast.error(response?.message, {
              className: "text-[12px]",
            });
          }
        })
        .catch((error) => {
          toast.error("Something went wrong!", {
            className: "text-[12px]",
          });
          console.error("Error during login:", error);
        })
        .finally(() => {
          setIsLoadingButton(false);
        });
    }
  };

  const exportToExcel_PCI_v4 = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Audit-Evidence1 Data");
    const headers = [
      "S.No.",
      "Req Id",
      "Requirement",
      "Requirement Description",
      "Section Id",
      "Section Description",
      "Control Id",
      "Control Description",
    ];
    worksheet.addRow(headers);
    standard.forEach((row, index) => {
      worksheet.addRow([
        index + 1,
        row.RequirementID,
        row.Requirement,
        row.RequirementDe,
        row.SectionID,
        row.SectionDescription,
        row.ControlID,
        row.ControlDescription,
      ]);
    });
    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "audit-evidence1_data.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const exportToExcel_PCI_v3 = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Audit-Evidence2 Data");
    const headers = [
      "S.No.",
      "Req Id",
      "Requirement",
      "Description",
      "Control Id",
      "Description",
    ];
    worksheet.addRow(headers);
    standard.forEach((row, index) => {
      worksheet.addRow([
        index + 1,
        row.RequirementID,
        row.RequirementTitle,
        row.RequirementDescription,
        row.ControlID,
        row.ControlDescription,
      ]);
    });
    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-evidence${selectedAuditId}_data.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div className="app-main__inner !pl-20 ">
      <Card
        className="h-full w-full rounded-3xl shadow-xl"
        style={{ backgroundImage: `url('/assets/images/beams.jpg')` }}
      >
        <CardHeader
          floated={false}
          shadow={false}
          className=" bg-transparent rounded-none !overflow-visible"
        >
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between md:gap-8">
            <div>
              <Typography variant="h5" color="blue-gray">
                List of Requirements
              </Typography>
              <Typography color="gray" className="mt-1 font-normal">
                View all requirements of the compliance standard
              </Typography>
            </div>
            <div className="flex flex-row md:flex-row items-center justify-end md:items-center gap-2 md:gap-8 mt-4 md:mt-0">
              <Button
                variant="outlined"
                size="sm"
                onClick={
                  selectedAuditStandard === "PCI DSS 4.0"
                    ? exportToExcel_PCI_v4
                    : exportToExcel_PCI_v3
                }
                className="mr-1"
                loading={isLoading === null ? true : false}
              >
                <ArrowDownIcon strokeWidth={2} className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
              <Select
                color="blue"
                className="!border-t-0 !border-[#b0bec5]"
                label="Select Limit"
                onChange={(event) => {
                  setLimit(event);
                  setIsLoading(null);
                }}
                value={limit}
              >
                <Option value={10}>10</Option>
                <Option value={20}>20</Option>
                <Option value={30}>30</Option>
                <Option value={40}>40</Option>
                <Option value={50}>50</Option>
              </Select>
              <Select
                color="purple"
                className="!border-t-0 !border-[#b0bec5]"
                label="Sort By"
                value={sortBy}
                onChange={(event) => {
                  setSortBy(event);
                  setIsLoading(null);
                }}
              >
                <Option value="asc">Ascending</Option>
                <Option value="desc">Descending</Option>
              </Select>
            </div>
            <div className="w-full md:w-auto">
              <Input
                label="Search"
                className="focus:!border-black focus:!border-2 focus:!border-t-0"
                value={searchBy}
                onChange={(e) => setSearchBy(e.target.value)}
                icon={<MagnifyingGlassIcon className="h-5 w-5" />}
              />
            </div>
          </div>
        </CardHeader>
        <CardBody className="overflow-scroll px-3 p-0">
          <table className="mt-4 w-full min-w-max table-auto text-left">
            <thead>
              <tr>
                {tableHead.map((head, index) => (
                  <th
                    key={head}
                    className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-3 transition-colors hover:bg-blue-gray-50 w-40"
                  >
                    <Typography
                      variant="small"
                      color="blue-gray"
                      className="flex items-center justify-between gap-2 font-bold leading-none opacity-70"
                    >
                      {head}{" "}
                      {index !== tableHead.length - 1 && (
                        <ChevronUpDownIcon
                          strokeWidth={2}
                          className="h-4 w-4"
                        />
                      )}
                    </Typography>
                  </th>
                ))}
              </tr>
            </thead>
            {isLoading === null ? (
              <>
                <tbody>
                  <tr>
                    <td colSpan={tableHead.length}>
                      <TableSkeleton />
                    </td>
                  </tr>
                </tbody>
              </>
            ) : (
              <>
                <tbody>
                  {isLoading === false && standard?.length > 0 ? (
                    standard.map((item, index) => {
                      if (!item) return null;
                      let RequirementID,
                        Requirement,
                        SectionID,
                        SectionDescription,
                        ControlID,
                        ControlDescription,
                        Collection,
                        RequirementDescription,
                        CollectionMasterName,
                        _id;

                      if (selectedAuditStandard === "PCI DSS v4.0") {
                        ({
                          RequirementID,
                          Requirement,
                          SectionID,
                          SectionDescription,
                          ControlID,
                          ControlDescription,
                          Collection,
                          _id,
                        } = item);
                      } else if (selectedAuditStandard === "PCI DSS v3.2.1") {
                        ({
                          RequirementID,
                          Requirement,
                          RequirementDescription,
                          ControlID,
                          ControlDescription,
                          CollectionMasterName,
                          _id,
                        } = item);
                      }
                      const isLast = index === templateData.length - 1;
                      const classes = isLast
                        ? "p-3"
                        : "p-3 border-b border-blue-gray-50";

                      return (
                        (selectedAuditStandard === "PCI DSS v4.0" && (
                          <tr key={`${_id}-${index}`}>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {RequirementID}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                <TableRowWithShowMore data={Requirement} />
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              ></Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {SectionID}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                <TableRowWithShowMore
                                  data={SectionDescription}
                                />
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {ControlID}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                <TableRowWithShowMore
                                  data={ControlDescription}
                                />
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Link to={`/evidenceById/${Collection}`}>
                                <Button variant="gradient" size="sm">
                                  Evidence
                                </Button>
                              </Link>
                            </td>
                            <td className={classes}>
                              <Checkbox
                                id="ripple-on"
                                ripple={true}
                                className="!border !border-black"
                                onChange={(e) =>
                                  handleCheckboxChange(e, ControlID, _id)
                                }
                              />
                            </td>
                          </tr>
                        )) ||
                        (selectedAuditStandard === "PCI DSS v3.2.1" && (
                          <tr key={`${_id}-${index}`}>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {RequirementID}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {Requirement.length > 40 ? (
                                  <TableRowWithShowMore data={Requirement} />
                                ) : (
                                  Requirement
                                )}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {RequirementDescription.length > 40 ? (
                                  <TableRowWithShowMore
                                    data={RequirementDescription}
                                  />
                                ) : (
                                  RequirementDescription
                                )}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {ControlID}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {ControlDescription.length > 40 ? (
                                  <TableRowWithShowMore
                                    data={ControlDescription}
                                  />
                                ) : (
                                  ControlDescription
                                )}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Link
                                to={`/evidenceById/${CollectionMasterName}`}
                              >
                                <Button variant="gradient">Evidence</Button>
                              </Link>
                            </td>
                            <td className={classes}>
                              <Checkbox
                                id="ripple-on"
                                ripple={true}
                                className="!border !border-black"
                                onChange={(e) =>
                                  handleCheckboxChange(e, ControlID, _id)
                                }
                              />
                            </td>
                          </tr>
                        ))
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8">
                        <div className="flex flex-col items-center w-full">
                          <img
                            quality={100}
                            src="/assets/images/9276430.jpg"
                            className="w-[350px] mix-blend-darken"
                            alt="no-data-found"
                          />
                          <p className="text-[20px] font-medium">
                            No Data Found!
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </>
            )}
          </table>
        </CardBody>
        {isLoading === null ? null : (
          <>
            <CardFooter className="border-t border-blue-gray-50 p-3">
              <div className="flex justify-center items-center pb-3">
                <Button
                  size="sm"
                  onClick={handleNextStep}
                  disabled={!selectCheckbox.length > 0}
                >
                  Next Step
                </Button>
              </div>
              <div className="flex items-center justify-between border-t border-blue-gray-50 p-3">
                <Button
                  variant="outlined"
                  size="sm"
                  onClick={() => {
                    setIsLoading(null);
                    setPage(page - 1);
                  }}
                  disabled={totalPage > 1 ? false : true}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-2">
                  {Array.from({ length: totalPage }, (_, index) => (
                    <IconButton
                      key={index + 1}
                      variant={index + 1 === page ? "outlined" : "text"}
                      size="sm"
                      onClick={() => {
                        setIsLoading(null);
                        setPage(index + 1);
                      }}
                      disabled={totalPage > 1 ? false : true}
                    >
                      {index + 1}
                    </IconButton>
                  ))
                    .reduce((acc, button, index, array) => {
                      if (
                        index < 3 ||
                        index >= totalPage - 3 ||
                        (index >= Math.max(page - 1, 3) &&
                          index <= Math.min(page + 1, totalPage - 4))
                      ) {
                        acc.push(button);
                      } else if (acc[acc.length - 1] !== "ellipsis") {
                        acc.push("ellipsis");
                      }
                      return acc;
                    }, [])
                    .map((element, index, array) => {
                      return element === "ellipsis" ? (
                        <IconButton
                          key={`ellipsis-${index}`}
                          variant="text"
                          size="sm"
                          disabled
                        >
                          ...
                        </IconButton>
                      ) : index > 0 && array[index - 1] === "ellipsis" ? (
                        <React.Fragment key={`fragment-${index}`}>
                          {element}
                        </React.Fragment>
                      ) : (
                        element
                      );
                    })}
                </div>
                <Button
                  variant="outlined"
                  size="sm"
                  onClick={() => {
                    setPage(page + 1);
                    setIsLoading(null);
                  }}
                  disabled={totalPage > 1 ? false : true}
                >
                  Next
                </Button>
              </div>
            </CardFooter>
          </>
        )}
      </Card>

      {/* Create Evidence Task Dialog */}
      <Dialog
        size="lg"
        open={open}
        handler={handleNextStep}
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <form onSubmit={handleSubmit}>
          <Card className="mx-auto w-full p-4">
            <CardBody className="flex flex-col gap-4">
              <Typography variant="h4" color="blue-gray">
                Create evidence task
              </Typography>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Typography className="mb-1" variant="h6">
                    Selected Control IDs
                  </Typography>
                  <div className="flex flex-row gap-4 flex-wrap">
                    {selectCheckbox.map((item) => (
                      <div
                        key={item.controlId}
                        className="flex items-center gap-4"
                      >
                        <Chip value={item.controlId}>{item.controlId}</Chip>
                        <Input
                          label="Enter evidence"
                          size="md"
                          name="TaskRequirements"
                          onChange={(e) => handleChange(e, item.controlId)}
                          onBlur={handleBlur}
                          type="text"
                          value={
                            formData.TaskRequirements.find(
                              (task) => task.id === item.controlId
                            )?.requirement || ""
                          }
                          className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                        />
                        {errors[item.controlId] && (
                          <p className="text-red-900 text-[12px]">
                            {errors[item.controlId]}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardBody>
            <CardFooter className="flex justify-end border-t-2 py-2">
              <Button
                variant="text"
                color="red"
                onClick={handleNextStep}
                className="mr-1"
              >
                <span>Cancel</span>
              </Button>

              <Button
                variant="gradient"
                color="green"
                type="submit"
                loading={isLoadingButton}
                disabled={!formData.TaskRequirements}
              >
                <span>Create</span>
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Dialog>
    </div>
  );
}
